import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data);
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  // Group policies by type
  const groupedPolicies = policies.reduce((acc, policy) => {
    if (!acc[policy.policy_type]) {
      acc[policy.policy_type] = [];
    }
    acc[policy.policy_type].push(policy);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-8" data-testid="policies-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Company Policies</h1>
          <p className="text-muted-foreground mt-2">View all company policies and guidelines</p>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : policies.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              No policies available yet
            </CardContent>
          </Card>
        ) : (
          Object.keys(groupedPolicies).map((policyType) => (
            <Card key={policyType} data-testid="policy-group-card">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="mr-2 h-5 w-5" />
                  {policyType}
                </CardTitle>
                <CardDescription>
                  {groupedPolicies[policyType].length} {groupedPolicies[policyType].length === 1 ? 'policy' : 'policies'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {groupedPolicies[policyType].map((policy) => (
                    <AccordionItem key={policy.id} value={policy.id} data-testid="policy-item">
                      <AccordionTrigger className="text-left">
                        <div>
                          <p className="font-medium">{policy.policy_title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Last updated: {new Date(policy.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="prose prose-sm max-w-none pt-4 whitespace-pre-wrap">
                          {policy.policy_content}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </Layout>
  );
}
